/**
 * useSeo — set document.title + meta tags + canonical for a public page.
 *
 * Library-free: directly mutates `<head>`. Each tag is identified by a
 * stable `data-seo-id` so subsequent route changes UPDATE existing tags
 * instead of stacking duplicates. Cleanup on unmount removes the
 * tags we created so they don't leak into the SA workspace pages.
 *
 * Also injects a JSON-LD blob describing the organization so search
 * engines pick up the legal name, address, EIN, and SC Transaction ID.
 */
import { useEffect } from "react";

function _setMeta(name, content, attr = "name") {
  if (!content) return null;
  const tagId = `seo-${attr}-${name}`;
  let el = document.querySelector(`meta[data-seo-id="${tagId}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("data-seo-id", tagId);
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function _setCanonical(href) {
  if (!href) return null;
  let el = document.querySelector('link[rel="canonical"][data-seo-id="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-seo-id", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function _setJsonLd(id, payload) {
  if (!payload) return null;
  let el = document.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
  return el;
}

export function useSeo({
  title,
  description,
  keywords = "Workforce Development, Business Consulting, Empowerment Training, Myrtle Beach SC",
  canonical,
  ogImage,
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;
    const created = [
      _setMeta("description", description),
      _setMeta("keywords",    keywords),
      _setMeta("og:title",        title || "", "property"),
      _setMeta("og:description",  description || "", "property"),
      _setMeta("og:type",         "website", "property"),
      _setMeta("og:image",        ogImage || "", "property"),
      _setMeta("twitter:card",    "summary_large_image"),
      _setCanonical(canonical),
      _setJsonLd("life-org", jsonLd),
    ].filter(Boolean);

    return () => {
      // We intentionally LEAVE these tags in place across route changes
      // (the next page will overwrite them). The only cleanup we do is
      // when the layout unmounts entirely (navigating away from the
      // public site into /admin or /life.llc).
      if (window.location.pathname.startsWith("/life-empowerment")) return;
      created.forEach((el) => el?.remove?.());
    };
  }, [title, description, keywords, canonical, ogImage, jsonLd]);
}

export function defaultOrgJsonLd(cfg) {
  if (!cfg) return null;
  const addr = cfg.address || {};
  return {
    "@context":  "https://schema.org",
    "@type":     "Organization",
    name:        cfg.legal_entity_name || "Learning Institute for Empowerment LLC",
    url:         (typeof window !== "undefined" ? window.location.origin : "") + "/life-empowerment",
    logo:        "https://customer-assets.emergentagent.com/job_exchange-landing/artifacts/b7b19klq_LOGO%20Reimagined.png",
    email:       cfg.public_email || "hello@life-empowerment.com",
    description: cfg.tagline || "Highlighting the Human Element, Then Empowering It.",
    taxID:       cfg.ein || "",
    legalName:   cfg.legal_entity_name || "Learning Institute for Empowerment LLC",
    address: addr.street ? {
      "@type":          "PostalAddress",
      streetAddress:    addr.street,
      addressLocality:  addr.city,
      addressRegion:    addr.state,
      postalCode:       addr.zip,
      addressCountry:   "US",
    } : undefined,
    founder: cfg.managing_member ? {
      "@type": "Person",
      name:    cfg.managing_member,
      jobTitle: cfg.managing_member_title || "Managing Member",
    } : undefined,
  };
}
